import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from '@/contexts/TranslationContext';

const LOGO_IMAGE = 'https://customer-assets.emergentagent.com/job_stray-care-1/artifacts/m4tj8pbh_1Smartpaw%20Post%20-%2034.PNG';

// Placeholder - Replace with actual Flitt payment link
const FLITT_PAYMENT_LINK = "https://flitt.com/pay/smartpawcare";

const FundraisingLandingPage = () => {
  const navigate = useNavigate();
  const { t, language } = useTranslation();

  const goals = [
    { 
      level: language === 'ge' ? 'მინიმალური მიზანი' : 'Minimum Goal',
      amount: '$6,500',
      description: language === 'ge' ? '5 საკვებური + 3-თვიანი ოპერაცია + საწყისი კამპანია' : '5 feeders + 3-month operations + launch campaign'
    },
    { 
      level: language === 'ge' ? 'გაფართოების მიზანი' : 'Stretch Goal',
      amount: '$15,000',
      description: language === 'ge' ? '15 საკვებური თბილისში' : '15 feeders across Tbilisi'
    },
    { 
      level: language === 'ge' ? 'სკალის მიზანი' : 'Scale Goal',
      amount: '$27,500',
      description: language === 'ge' ? '30 საკვებური + სრული რეგიონული PR' : '30 feeders + full regional PR rollout'
    },
    { 
      level: language === 'ge' ? 'ოცნების მიზანი' : 'Dream Goal',
      amount: '$45,000',
      description: language === 'ge' ? '50 საკვებური - საქართველოს სრული დაფარვა + აქტიური PR კამპანიები' : '50 feeders — complete Georgia coverage + active PR campaigns'
    }
  ];

  const features = [
    {
      icon: '☀️',
      title: language === 'ge' ? 'მზის ენერგია' : 'Solar Powered',
      description: language === 'ge' ? 'მდგრადი ენერგია, მინიმალური გარე კვება' : 'Sustainable energy, minimal external power'
    },
    {
      icon: '📡',
      title: language === 'ge' ? 'IoT დაკავშირებული' : 'IoT Connected',
      description: language === 'ge' ? 'ღრუბლოვანი სინქრონიზაცია, მონიტორინგი' : 'Cloud sync, usage monitoring'
    },
    {
      icon: '🎯',
      title: language === 'ge' ? 'ჭკვიანი დოზირება' : 'Smart Dispenser',
      description: language === 'ge' ? 'კონტროლირებადი პორციები, არანაირი ნარჩენი' : 'Controlled portions, no waste'
    },
    {
      icon: '📱',
      title: language === 'ge' ? 'QR გადახდა' : 'QR Payment',
      description: language === 'ge' ? 'ნებისმიერს შეუძლია დახმარება - დაასკანირე და გააკეთე' : 'Anyone can help — scan & feed'
    },
    {
      icon: '🛡️',
      title: language === 'ge' ? 'უჟანგავი თასი' : 'Stainless Steel Bowl',
      description: language === 'ge' ? 'ჰიგიენური, გამძლე, მარტივად გასაწმენდი' : 'Hygienic, durable, easy to clean'
    }
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <img src={LOGO_IMAGE} alt="SmartPaw Care" className="h-12" />
            <span className="font-['Outfit'] text-xl font-bold text-gray-900">SmartPaw Care</span>
          </div>
          <button
            onClick={() => window.open(FLITT_PAYMENT_LINK, '_blank')}
            className="px-6 py-3 bg-[#F4A460] hover:bg-[#E89350] text-white font-['Manrope'] font-semibold rounded-full transition-all duration-300 shadow-lg"
          >
            {language === 'ge' ? '💝 დონაცია' : '💝 Donate Now'}
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#1e3a5f] to-[#2d5a8f] text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')]"></div>
        </div>
        
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="font-['Outfit'] text-5xl md:text-7xl font-bold mb-6 leading-tight">
              {language === 'ge' ? (
                <>ჭკვიანი ზრუნვა.<br />რეალური ეფექტი.</>
              ) : (
                <>Smart Care.<br />Real Impact.</>
              )}
            </h1>
            <p className="font-['Manrope'] text-xl md:text-2xl mb-4 text-white/90 max-w-3xl mx-auto leading-relaxed">
              {language === 'ge' 
                ? 'ჭკვიანი კვება საქართველოს მიუსაფრებისთვის. უფრო სუფთა ქუჩები. ჯანსაღი ცხოველები. ძლიერი თემები.'
                : 'Smarter feeding for Georgia\'s strays. Cleaner streets. Healthier animals. Stronger communities.'
              }
            </p>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => window.open(FLITT_PAYMENT_LINK, '_blank')}
              className="mt-8 px-10 py-5 bg-[#F4A460] hover:bg-[#E89350] text-white font-['Manrope'] text-lg font-bold rounded-full transition-all duration-300 shadow-2xl"
            >
              {language === 'ge' ? '💝 დონაცია ახლავე' : '💝 Donate Now'}
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-['Outfit'] text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {language === 'ge' ? 'პრობლემა: 600,000+ მიუსაფარი' : 'The Problem: 600,000+ Strays'}
            </h2>
            <p className="font-['Manrope'] text-xl text-gray-600 max-w-3xl mx-auto">
              {language === 'ge' 
                ? 'კეთილი განზრახვა. არასწორი პრაქტიკა.'
                : 'Good Intent. Wrong Practice.'
              }
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-red-50 p-8 rounded-2xl border-l-4 border-red-500"
            >
              <div className="text-4xl mb-4">🍽️</div>
              <h3 className="font-['Outfit'] text-xl font-bold text-gray-900 mb-3">
                {language === 'ge' ? 'არარეგულარული კვება' : 'Irregular Feeding'}
              </h3>
              <p className="font-['Manrope'] text-gray-700">
                {language === 'ge' 
                  ? 'ცხოველები იკვებებიან დაუგეგმავად და ადგილის გარეშე — ეს იწვევს ქრონიკულ სტრესს, შიმშილით გამოწვეულ აგრესიას და ჯანმრთელობის გაუარესებას.'
                  : 'Animals fed with no fixed schedule or location — causing chronic stress, hunger-driven aggression, and deteriorating health.'
                }
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-orange-50 p-8 rounded-2xl border-l-4 border-orange-500"
            >
              <div className="text-4xl mb-4">🗑️</div>
              <h3 className="font-['Outfit'] text-xl font-bold text-gray-900 mb-3">
                {language === 'ge' ? 'პლასტმასი და ნარჩენები' : 'Plastic & Waste'}
              </h3>
              <p className="font-['Manrope'] text-gray-700">
                {language === 'ge' 
                  ? 'მიუსაფრები იკვებებიან ცელოფნის პარკებიდან და პლასტმასის ჭურჭლებიდან — ეს ქმნის საკვების ნარჩენებსა და ნაგავს საცხოვრებელ უბნებში.'
                  : 'Strays fed from cellophane bags and plastic containers left on the ground — creating food waste and litter across residential areas.'
                }
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-yellow-50 p-8 rounded-2xl border-l-4 border-yellow-500"
            >
              <div className="text-4xl mb-4">⚠️</div>
              <h3 className="font-['Outfit'] text-xl font-bold text-gray-900 mb-3">
                {language === 'ge' ? 'იძულებითი გადასახლება' : 'Forced Relocations'}
              </h3>
              <p className="font-['Manrope'] text-gray-700">
                {language === 'ge' 
                  ? 'მუნიციპალიტეტები იწყებენ მიუსაფრების დაჭერას მათი ჰაბიტატებიდან, აგრესიისა და დაბინძურების მითითებით. თემებმა დიდი რაოდენობით გააპროტესტეს ეს.'
                  : 'Municipal authorities rounding up strays from their habitats, citing aggression and pollution. Communities have protested in large numbers.'
                }
              </p>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="bg-gray-900 text-white p-8 rounded-2xl text-center"
          >
            <p className="font-['Manrope'] text-lg md:text-xl">
              {language === 'ge' ? (
                <>
                  <span className="font-bold text-[#F4A460]">600,000+</span> მიუსაფარი საქართველოში. <span className="font-bold text-[#F4A460]">1.2 მილიონ+</span> კავკასიაში.<br />
                  ფანჯარა ჭკვიანი გადაწყვეტის დემონსტრირებისთვის — შემდგომი პოლიტიკის ქმედებამდე — არის ახლა.
                </>
              ) : (
                <>
                  <span className="font-bold text-[#F4A460]">600K+</span> strays in Georgia. <span className="font-bold text-[#F4A460]">1.2M+</span> across the Caucasus.<br />
                  The window to demonstrate a smarter solution — before further policy action — is now.
                </>
              )}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 bg-gradient-to-b from-[#FAFAF7] to-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-['Outfit'] text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {language === 'ge' ? 'ჩვენი გადაწყვეტა: SmartPaw საკვებური' : 'Our Solution: The SmartPaw Feeder'}
            </h2>
            <p className="font-['Manrope'] text-xl text-gray-600 max-w-3xl mx-auto">
              {language === 'ge' 
                ? 'გამძლე, ამინდგამძლე გარე მოწყობილობა, დამონტაჟებული ცხოველებისთვის მეგობრულ ბიზნესებში. იგი ცვლის ქაოსურ, დამაბინძურებელ კვებას სუფთა, სისტემატური, თემის მიერ მხარდაჭერილ ინფრასტრუქტურით.'
                : 'A durable, weather-resistant outdoor device installed at pet-friendly businesses. Replacing chaotic, polluting feeding with clean, systematic, community-powered infrastructure.'
              }
            </p>
          </motion.div>

          <div className="grid md:grid-cols-5 gap-6 mb-12">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow text-center"
              >
                <div className="text-4xl mb-3">{feature.icon}</div>
                <h3 className="font-['Outfit'] font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="font-['Manrope'] text-sm text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="bg-[#F4A460] text-white p-8 rounded-2xl text-center"
          >
            <p className="font-['Manrope'] text-2xl font-bold">
              {language === 'ge' 
                ? '$845 ერთი საკვებურის წარმოება · $100/თვე ერთი საკვებურის ოპერირება'
                : '$845 to produce one feeder · $100/month to operate one feeder'
              }
            </p>
          </motion.div>
        </div>
      </section>

      {/* Before/After Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-['Outfit'] text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {language === 'ge' ? 'პრობლემიდან გადაწყვეტამდე — მოქმედებაში' : 'From Problem to Solution — In Action'}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative rounded-2xl overflow-hidden shadow-2xl"
            >
              <div className="absolute top-4 left-4 bg-red-500 text-white px-4 py-2 rounded-full font-['Manrope'] font-bold z-10">
                {language === 'ge' ? 'მანამდე' : 'BEFORE'}
              </div>
              <div className="bg-gray-200 h-80 flex items-center justify-center">
                <p className="text-gray-500 font-['Manrope'] text-center p-8">
                  {language === 'ge' 
                    ? '[პლასტმასის ჭურჭელი, საკვების ნარჩენები მიწაზე]'
                    : '[Plastic container, food waste on ground]'
                  }
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative rounded-2xl overflow-hidden shadow-2xl"
            >
              <div className="absolute top-4 left-4 bg-green-500 text-white px-4 py-2 rounded-full font-['Manrope'] font-bold z-10">
                {language === 'ge' ? 'შემდეგ' : 'AFTER'}
              </div>
              <div className="bg-gray-200 h-80 flex items-center justify-center">
                <p className="text-gray-500 font-['Manrope'] text-center p-8">
                  {language === 'ge' 
                    ? '[SmartPaw საკვებური, სუფთა დოზირება, მიუსაფარი ჭამს უჟანგავი თასიდან]'
                    : '[SmartPaw Feeder, clean dispensing, stray eating from stainless bowl]'
                  }
                </p>
              </div>
            </motion.div>
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="font-['Manrope'] text-lg text-gray-700 text-center mt-12 max-w-3xl mx-auto"
          >
            {language === 'ge' 
              ? 'პარტნიორი კაფეები და სხვა ადგილობრივი ბიზნესები არიან ბუნებრივი მასპინძლები — უკვე უბნის ნაწილი, უკვე სანდო ცხოველებისა და ადამიანებისთვის, რომლებიც ზრუნავენ მათზე.'
              : 'Partner cafés and local businesses are natural hosts — already part of the neighborhood, already trusted by the animals and people who care for them.'
            }
          </motion.p>
        </div>
      </section>

      {/* Why Now Section */}
      <section className="py-20 bg-gradient-to-b from-[#1e3a5f] to-[#2d5a8f] text-white">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="font-['Outfit'] text-4xl md:text-5xl font-bold mb-12">
              {language === 'ge' ? 'რატომ ახლა?' : 'Why Now?'}
            </h2>
            
            <div className="space-y-6 text-left">
              <div className="flex gap-4">
                <span className="text-3xl">📊</span>
                <p className="font-['Manrope'] text-lg">
                  {language === 'ge' 
                    ? '600,000+ მიუსაფარი მხოლოდ საქართველოში — ორჯერ მეტი კავკასიაში'
                    : '600K+ strays in Georgia alone — more than double across the Caucasus'
                  }
                </p>
              </div>
              
              <div className="flex gap-4">
                <span className="text-3xl">⚠️</span>
                <p className="font-['Manrope'] text-lg">
                  {language === 'ge' 
                    ? 'მზარდი კრიზისი — ხელისუფლება იძულებით გადაასახლებს მიუსაფრებს, აგრესიისა და დაბინძურების მითითებით'
                    : 'A growing crisis — authorities forcibly relocating strays, citing aggression and pollution'
                  }
                </p>
              </div>
              
              <div className="flex gap-4">
                <span className="text-3xl">✊</span>
                <p className="font-['Manrope'] text-lg">
                  {language === 'ge' 
                    ? 'თემის წინააღმდეგობა — დიდი საჯარო აქციები აჩვენებს, რამდენად ღრმად ჩართულია თემები მიუსაფრების კეთილდღეობაში'
                    : 'Community resistance — large public protests show how deeply invested communities are in stray welfare'
                  }
                </p>
              </div>
              
              <div className="flex gap-4">
                <span className="text-3xl">⏰</span>
                <p className="font-['Manrope'] text-lg">
                  {language === 'ge' 
                    ? 'ფანჯარა არის ახლა — ჭკვიანი გადაწყვეტის დემონსტრირება შემდგომ პოლიტიკურ ქმედებამდე არის გადამწყვეტი'
                    : 'The window is now — demonstrating a smarter solution before further policy action is urgent'
                  }
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Roadmap Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-['Outfit'] text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {language === 'ge' ? 'ჩვენი გზა' : 'Our Roadmap'}
            </h2>
          </motion.div>

          <div className="space-y-8">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="flex gap-6 items-start"
            >
              <div className="flex-shrink-0 w-24 h-24 bg-[#F4A460] rounded-full flex items-center justify-center">
                <span className="font-['Outfit'] text-2xl font-bold text-white">2026</span>
              </div>
              <div className="flex-1 bg-gray-50 p-6 rounded-xl">
                <h3 className="font-['Outfit'] text-2xl font-bold text-gray-900 mb-3">
                  {language === 'ge' ? 'საქართველო' : 'Georgia'}
                </h3>
                <p className="font-['Manrope'] text-gray-700">
                  {language === 'ge' 
                    ? 'საკვებურების გაშვება თბილისში · პარტნიორი ბიზნესების ქსელის შექმნა · PR და ცნობადობის კამპანიები · განმეორებადი QR გადახდის მოდელი'
                    : 'Launch feeders in Tbilisi · Build partner business network · PR & awareness campaigns · Recurring QR payment model'
                  }
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="flex gap-6 items-start"
            >
              <div className="flex-shrink-0 w-24 h-24 bg-blue-600 rounded-full flex items-center justify-center">
                <span className="font-['Outfit'] text-2xl font-bold text-white">2027</span>
              </div>
              <div className="flex-1 bg-gray-50 p-6 rounded-xl">
                <h3 className="font-['Outfit'] text-2xl font-bold text-gray-900 mb-3">
                  {language === 'ge' ? 'მასშტაბირება' : 'Scale Up'}
                </h3>
                <p className="font-['Manrope'] text-gray-700">
                  {language === 'ge' 
                    ? 'გაფართოება 10+ ქართულ ქალაქში · 50+ მოწყობილობის განთავსება · მდგრადი ოპერაციული დაფინანსების მოდელი · პოლიტიკის ჩართულობა'
                    : 'Expand to 10+ Georgian cities · 50+ devices deployed · Sustainable recurring ops funding · Policy engagement'
                  }
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="flex gap-6 items-start"
            >
              <div className="flex-shrink-0 w-24 h-24 bg-green-600 rounded-full flex items-center justify-center">
                <span className="font-['Outfit'] text-2xl font-bold text-white">2028</span>
              </div>
              <div className="flex-1 bg-gray-50 p-6 rounded-xl">
                <h3 className="font-['Outfit'] text-2xl font-bold text-gray-900 mb-3">
                  {language === 'ge' ? 'კავკასია' : 'Caucasus'}
                </h3>
                <p className="font-['Manrope'] text-gray-700">
                  {language === 'ge' 
                    ? 'სომხეთისა და აზერბაიჯანის პილოტური გაშვება · რეგიონული ცნობადობის კამპანიები · ტრანსსასაზღვრო NGO და პოლიტიკის ადვოკატირების პარტნიორობა'
                    : 'Armenia & Azerbaijan pilot launches · Regional awareness campaigns · Cross-border NGO and policy advocacy partnerships'
                  }
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-[#FAFAF7]">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="font-['Outfit'] text-4xl md:text-5xl font-bold text-gray-900 mb-8">
              {language === 'ge' ? 'SmartPaw-ის შესახებ' : 'About SmartPaw'}
            </h2>
            <div className="font-['Manrope'] text-lg text-gray-700 space-y-4 text-left">
              <p>
                {language === 'ge' 
                  ? 'SmartPaw არის ქართული ტექნოლოგიური სტარტაპი, დაარსებული 2025 წელს, რომ მოდერნიზებულიყო შინაური ცხოველების კვება ჭკვიანი ტექნოლოგიით — და ჩვენ მოვიგეთ რამდენიმე ეროვნული სტარტაპის კონკურსი გზაზე.'
                  : 'SmartPaw is a Georgian technology startup, founded in 2025 to modernize pet feeding through smart technology — and we won several national startup competitions along the way.'
                }
              </p>
              <p>
                {language === 'ge' 
                  ? 'როდესაც საქართველოს მიუსაფარი ცხოველების კრიზისი გამძაფრდა, ჩვენ შევცვალეთ ფოკუსი. ჩვენი ტექნოლოგია იყო სწორი ინსტრუმენტი გაცილებით დიდი პრობლემისთვის.'
                  : 'When Georgia\'s stray animal crisis escalated, we redirected our focus. Our technology was the right tool for a much bigger problem.'
                }
              </p>
              <p>
                {language === 'ge' 
                  ? 'Smart Care. Real Impact. სწრაფად გაიზარდა, დააერთა ცხოველების მოყვარული ინდივიდები, ბიზნესები და თემური ადვოკატები ერთ კოორდინირებულ პროგრამად — და ჩვენ ვფართოვდებით სომხეთსა და აზერბაიჯანში ორი წლის განმავლობაში.'
                  : 'Smart Care. Real Impact. has grown rapidly, bringing together pet-loving individuals, businesses, and community advocates into a single coordinated program — and we\'re expanding to Armenia and Azerbaijan within two years.'
                }
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Funding Goals Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-['Outfit'] text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {language === 'ge' ? 'დაფინანსების თხოვნა' : 'Funding Ask'}
            </h2>
            <p className="font-['Manrope'] text-xl text-gray-600 max-w-3xl mx-auto">
              {language === 'ge' 
                ? 'ყოველი ლარი აშენებს რეალურ ინფრასტრუქტურას'
                : 'Every Dollar Builds Real Infrastructure'
              }
            </p>
            <p className="font-['Manrope'] text-gray-600 mt-4 max-w-3xl mx-auto">
              {language === 'ge' 
                ? 'საოპერაციო ხარჯები — საკვების მიწოდება, მოვლა — დაფარულია თემის QR გადახდებით. თქვენი დონაცია მიდის ექსკლუზიურად მოწყობილობის წარმოებაზე, დაყენებაზე და ცნობადობის კამპანიებზე.'
                : 'Operational costs — food supply, maintenance — are covered by QR payments from the community. Your donation goes exclusively toward device production, installation, and awareness campaigns.'
              }
            </p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-6 mb-12">
            {goals.map((goal, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`p-6 rounded-2xl ${
                  index === 0 
                    ? 'bg-gradient-to-br from-[#F4A460] to-[#E89350] text-white' 
                    : 'bg-gray-50 border-2 border-gray-200'
                }`}
              >
                <p className={`font-['Manrope'] text-sm mb-2 ${index === 0 ? 'text-white/90' : 'text-gray-600'}`}>
                  {goal.level}
                </p>
                <p className={`font-['Outfit'] text-3xl font-bold mb-3 ${index === 0 ? 'text-white' : 'text-gray-900'}`}>
                  {goal.amount}
                </p>
                <p className={`font-['Manrope'] text-sm ${index === 0 ? 'text-white/90' : 'text-gray-600'}`}>
                  {goal.description}
                </p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <p className="font-['Manrope'] text-gray-600 italic mb-8">
              {language === 'ge' 
                ? 'ყველა დონაცია მიზნის ზემოთ მიდის დამატებით მოწყობილობებსა და გაფართოებულ კამპანიებზე.'
                : 'All donations above goal go toward additional devices and expanded campaigns.'
              }
            </p>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => window.open(FLITT_PAYMENT_LINK, '_blank')}
              className="px-12 py-5 bg-[#F4A460] hover:bg-[#E89350] text-white font-['Manrope'] text-xl font-bold rounded-full transition-all duration-300 shadow-2xl"
            >
              {language === 'ge' ? '💝 დონაცია ახლავე' : '💝 Donate Now'}
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#1e3a5f] to-[#2d5a8f] text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-['Outfit'] text-4xl md:text-5xl font-bold mb-6">
              {language === 'ge' ? 'იყავი გადაწყვეტის ნაწილი' : 'Be Part of the Solution'}
            </h2>
            <p className="font-['Manrope'] text-xl mb-10 text-white/90">
              {language === 'ge' 
                ? 'ყოველი განთავსებული საკვებური არის მუდმივი ფიქსაცია ბრძოლაში უკეთესი მიუსაფარი კეთილდღეობისთვის — და უფრო სუფთა, კეთილი ქალაქებისთვის.'
                : 'Every feeder placed is a permanent fixture in the fight for better stray welfare — and cleaner, kinder cities.'
              }
            </p>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => window.open(FLITT_PAYMENT_LINK, '_blank')}
              className="px-12 py-5 bg-[#F4A460] hover:bg-[#E89350] text-white font-['Manrope'] text-xl font-bold rounded-full transition-all duration-300 shadow-2xl"
            >
              {language === 'ge' ? '💝 დონაცია ახლავე' : '💝 Donate Now'}
            </motion.button>

            <div className="mt-12 pt-12 border-t border-white/20">
              <p className="font-['Manrope'] text-white/70">
                {language === 'ge' 
                  ? 'SmartPaw Care-ს მართავს CleanPaw International LLC'
                  : 'SmartPaw Care is operated by CleanPaw International LLC'
                }
              </p>
              <p className="font-['Manrope'] text-white/70 mt-2">smartcare.ge</p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FundraisingLandingPage;
